import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Sparkline (mini trend chart) ──
const Sparkline = ({ data, dataKey, color = "#10b981", height = 40, width = 120 }) => {
  if (!data || data.length < 2) return <span className="text-xs text-slate-600 italic">Insufficient data</span>;

  const values = data.map((d) => d[dataKey] || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      {/* Current value dot */}
      {values.length > 0 && (() => {
        const lastIdx = values.length - 1;
        const cx = (lastIdx / (values.length - 1)) * width;
        const cy = height - ((values[lastIdx] - min) / range) * (height - 4) - 2;
        return <circle cx={cx} cy={cy} r={3} fill={color} />;
      })()}
    </svg>
  );
};

// ── Metric Card ──
const MetricCard = ({ title, value, unit, subtitle, trend, trendData, trendKey, color }) => {
  const isPositive = trend > 0;
  const trendColor = isPositive ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-slate-500";

  return (
    <div className="stat-card-hero space-y-3">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{title}</p>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="stat-number text-white">
            {value}
            {unit && <span className="text-lg text-slate-400 font-normal ml-1" style={{textShadow: 'none'}}>{unit}</span>}
          </p>
          {subtitle && <p className="text-xs text-slate-500 mt-2">{subtitle}</p>}
          {trend !== undefined && trend !== null && (
            <p className={`text-xs mt-1 ${trendColor}`}>
              {isPositive ? "+" : ""}{trend.toFixed(1)}% vs last period
            </p>
          )}
        </div>
        {trendData && (
          <Sparkline data={trendData} dataKey={trendKey} color={color || "#00d4d4"} />
        )}
      </div>
    </div>
  );
};

// ── Operator Row ──
const OperatorMetricRow = ({ op, trends }) => {
  const opTrend = trends.filter((t) => t.operator_id === op.operator_id);

  return (
    <div
      data-testid={`operator-metric-row-${op.operator_id}`}
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 grid grid-cols-4 gap-4 items-center"
    >
      <div>
        <p className="text-sm font-medium text-white">{op.operator_name || op.operator_id}</p>
        <p className="text-xs text-slate-500">Period: {op.period}</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-blue-400">{op.discovery_to_launch_avg_days}</p>
        <p className="text-xs text-slate-500">D2L days</p>
        <Sparkline data={opTrend} dataKey="discovery_to_launch_avg_days" color="#3b82f6" width={80} height={24} />
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-emerald-400">{op.test_cycle_per_week}/wk</p>
        <p className="text-xs text-slate-500">{op.test_cycle_per_month}/mo</p>
        <Sparkline data={opTrend} dataKey="test_cycle_per_week" color="#10b981" width={80} height={24} />
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-amber-400">{op.intelligence_conversion_pct}%</p>
        <p className="text-xs text-slate-500">ICR</p>
        <Sparkline data={opTrend} dataKey="intelligence_conversion_pct" color="#f59e0b" width={80} height={24} />
      </div>
    </div>
  );
};

// ============================================================================
// MAIN VELOCITY DASHBOARD
// ============================================================================
const VelocityDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [operatorMetrics, setOperatorMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/velocity/metrics`),
        axios.get(`${API}/velocity/trends?periods=6`),
      ]);
      setMetrics(metricsRes.data);
      setTrends(trendsRes.data.trends || []);

      // Fetch per-operator stored velocity data
      const velocityDocs = await axios.get(`${API}/velocity/trends?periods=12`);
      const allDocs = velocityDocs.data.trends || [];
      // Get unique non-aggregate operator docs
      const latestByOp = {};
      for (const doc of allDocs) {
        if (doc.operator_id === "aggregate") continue;
        if (!latestByOp[doc.operator_id] || doc.period > latestByOp[doc.operator_id].period) {
          latestByOp[doc.operator_id] = doc;
        }
      }
      setOperatorMetrics(Object.values(latestByOp));
    } catch (err) {
      console.error("Failed to fetch velocity data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCompute = async () => {
    setComputing(true);
    try {
      await axios.post(`${API}/velocity/compute`);
      await fetchData();
    } catch (err) {
      alert("Failed to compute: " + (err.response?.data?.detail || err.message));
    }
    setComputing(false);
  };

  const d2l = metrics?.discovery_to_launch || {};
  const tcf = metrics?.test_cycle_frequency || {};
  const icr = metrics?.intelligence_conversion_rate || {};

  // Calculate trends
  const calcTrend = (trendData, key) => {
    if (!trendData || trendData.length < 2) return null;
    const recent = trendData[trendData.length - 1]?.[key] || 0;
    const prev = trendData[trendData.length - 2]?.[key] || 0;
    if (prev === 0) return null;
    return ((recent - prev) / prev) * 100;
  };

  return (
    <div className="space-y-6" data-testid="velocity-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="velocity-title">Feedback Velocity</h2>
          <p className="text-slate-400 text-sm mt-1">
            Operational speed metrics — how fast intelligence converts to action
          </p>
        </div>
        <button
          onClick={handleCompute}
          disabled={computing}
          data-testid="compute-velocity-btn"
          className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors text-sm"
        >
          {computing ? "Computing..." : "Compute & Store"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Aggregate Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Discovery-to-Launch Velocity"
              value={d2l.avg_days || 0}
              unit="days"
              subtitle={`${d2l.sample_count || 0} offers measured`}
              trend={calcTrend(trends, "discovery_to_launch_avg_days")}
              trendData={trends}
              trendKey="discovery_to_launch_avg_days"
              color="#3b82f6"
            />
            <MetricCard
              title="Test Cycle Frequency"
              value={tcf.avg_per_week || 0}
              unit="/week"
              subtitle={`${tcf.total_tests || 0} total tests · ${tcf.avg_per_month || 0}/month`}
              trend={calcTrend(trends, "test_cycle_per_week")}
              trendData={trends}
              trendKey="test_cycle_per_week"
              color="#10b981"
            />
            <MetricCard
              title="Intelligence Conversion Rate"
              value={icr.conversion_rate_pct || 0}
              unit="%"
              subtitle={`${icr.accepted_in_time || 0}/${icr.total_recommendations || 0} accepted in time`}
              trend={calcTrend(trends, "intelligence_conversion_pct")}
              trendData={trends}
              trendKey="intelligence_conversion_pct"
              color="#f59e0b"
            />
          </div>

          {/* Detailed Breakdown */}
          {icr.total_recommendations > 0 && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Intelligence Decision Breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{icr.total_recommendations}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-emerald-400">{icr.accepted_total}</p>
                  <p className="text-xs text-slate-500">Accepted</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-400">{icr.overridden}</p>
                  <p className="text-xs text-slate-500">Overridden</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-400">{icr.ignored}</p>
                  <p className="text-xs text-slate-500">Ignored</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-red-400">{icr.expired_pending}</p>
                  <p className="text-xs text-slate-500">Expired</p>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Breakdown Chart (text-based) */}
          {tcf.weekly_breakdown && Object.keys(tcf.weekly_breakdown).length > 0 && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Weekly Test Volume</p>
              <div className="flex items-end gap-1 h-24">
                {Object.entries(tcf.weekly_breakdown).map(([week, count]) => {
                  const maxCount = Math.max(...Object.values(tcf.weekly_breakdown), 1);
                  const pct = (count / maxCount) * 100;
                  return (
                    <div key={week} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-slate-400 font-mono">{count}</span>
                      <div
                        className="w-full bg-emerald-500/40 rounded-t"
                        style={{ height: `${Math.max(pct, 4)}%` }}
                      />
                      <span className="text-[10px] text-slate-600 truncate w-full text-center">{week.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-Operator Metrics */}
          {operatorMetrics.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Per-Operator Performance</p>
              <div className="grid grid-cols-4 gap-4 px-4 text-xs text-slate-600 uppercase">
                <span>Operator</span>
                <span className="text-center">D2L Velocity</span>
                <span className="text-center">Test Frequency</span>
                <span className="text-center">Conversion Rate</span>
              </div>
              {operatorMetrics.map((op) => (
                <OperatorMetricRow key={op.operator_id} op={op} trends={trends} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!metrics && (
            <div className="text-center py-16 bg-slate-900/40 border border-slate-800 rounded-xl" data-testid="velocity-empty">
              <p className="text-slate-500 text-sm">No velocity data available. Click Compute & Store to generate.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VelocityDashboard;
